import React, { useState, useEffect } from 'react';
import { getAvatarUrl } from '../utils/avatarUtils';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  UserPlus, UserCheck, Clock, Share2, 
  Target, Award, BookOpen, Brain, 
  Gamepad2, UserMinus, ShieldAlert,
  Swords, AlertCircle
} from 'lucide-react';
import { 
  getUserProfileStats, 
  checkFriendRequestStatus, 
  sendFriendRequest, 
  acceptFriendRequest,
  removeFriend,
  SocialUser
} from '../services/socialService';
import { AchievementService } from '../services/achievementService';


const PublicProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState<any>(null);
  const [userAchievements, setUserAchievements] = useState<any>(null);
  const [friendStatus, setFriendStatus] = useState<'none' | 'sent' | 'received' | 'friends'>('none');
  const [loading, setLoading] = useState(true);
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  useEffect(() => {
    if (!userId) return;

    const loadProfile = async () => {
      setLoading(true);
      try {
        const data = await getUserProfileStats(userId);
        setProfileData(data);

        const achievementService = AchievementService.getInstance({ uid: userId });
        const achievementsData = await achievementService.getUserAchievements();
        setUserAchievements(achievementsData);

        if (currentUser && currentUser.uid !== userId) {
          const status = await checkFriendRequestStatus(currentUser.uid, userId);
          setFriendStatus(status);
        } else if (currentUser && currentUser.uid === userId) {
            setFriendStatus('none'); // Own profile
        }
      } catch (err) {
        console.error('Failed to load profile', err);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [userId, currentUser]);

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: `${profileData?.user?.username}'s Speakly Profile`,
        url: url
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(url);
      alert('Profile link copied to clipboard!');
    }
  };

  const handleAction = async () => {
    if (!currentUser || !userId) return;
    
    setIsProcessingAction(true);
    try {
      if (friendStatus === 'none') {
        await sendFriendRequest(currentUser.uid, userId);
        setFriendStatus('sent');
      } else if (friendStatus === 'received') {
        await acceptFriendRequest(currentUser.uid, userId);
        setFriendStatus('friends');
      } else if (friendStatus === 'friends') {
        if (window.confirm('Are you sure you want to remove this friend?')) {
           await removeFriend(currentUser.uid, userId);
           setFriendStatus('none');
        }
      }
    } catch (err) {
      console.error('Action failed', err);
    } finally {
      setIsProcessingAction(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!profileData || !profileData.user) {
    return (
      <div className="text-center py-20 text-gray-500">
        <ShieldAlert className="w-16 h-16 mx-auto mb-4 text-gray-400" />
        <h2 className="text-2xl font-bold">User Not Found</h2>
      </div>
    );
  }

  const { user, stats, daysWithoutStudy } = profileData;
  const isOwnProfile = currentUser?.uid === userId;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6 pb-20 md:pb-8">
      {/* Profile Header */}
      <div className="bg-white rounded-3xl p-6 md:p-10 shadow-lg border border-gray-100 flex flex-col md:flex-row items-center md:items-start gap-8 relative overflow-hidden">
        
        {/* Background Decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50 rounded-full blur-3xl -z-10 translate-x-1/2 -translate-y-1/2 opacity-60"></div>
        
        <div className="shrink-0 relative">
          <img referrerPolicy="no-referrer" 
            src={getAvatarUrl(user.profilePhotoUrl || user.id)} 
            alt={user.username} 
            className="w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-white shadow-xl object-cover"
          />
          <div className="absolute -bottom-2 -right-2 bg-indigo-600 text-white p-2 rounded-full shadow-lg">
            <Award className="w-6 h-6" />
          </div>
        </div>

        <div className="flex-1 text-center md:text-left space-y-4 w-full">
          <div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
              {user.username}
            </h1>
            <p className="text-indigo-600 font-medium text-lg mt-1 flex items-center justify-center md:justify-start gap-2">
              <Target className="w-5 h-5" /> 
              {user.totalPoints} Total Points
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 pt-2">
            {!isOwnProfile && currentUser && (
              <button 
                onClick={handleAction}
                disabled={isProcessingAction}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed ${
                  friendStatus === 'none' 
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700 hover:scale-105' 
                    : friendStatus === 'sent'
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : friendStatus === 'received'
                    ? 'bg-green-500 text-white hover:bg-green-600 hover:scale-105'
                    : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                }`}
              >
                {isProcessingAction ? (
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    {friendStatus === 'none' && <><UserPlus className="w-5 h-5" /> Add Friend</>}
                    {friendStatus === 'sent' && <><Clock className="w-5 h-5" /> Request Sent</>}
                    {friendStatus === 'received' && <><UserCheck className="w-5 h-5" /> Accept Request</>}
                    {friendStatus === 'friends' && <><UserCheck className="w-5 h-5" /> Friends</>}
                  </>
                )}
              </button>
            )}

            {!isOwnProfile && currentUser && friendStatus === 'friends' && (
              <>
                <button
                  onClick={() => navigate('/social', { state: { challengeUserId: userId, activeTab: 'friends' } })}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 hover:scale-105 transition-all shadow-sm"
                >
                  <Swords className="w-5 h-5" /> Challenge
                </button>
                <button
                  onClick={() => navigate('/social', { state: { provokeUserId: userId, activeTab: 'friends' } })}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 hover:scale-105 transition-all shadow-sm"
                >
                  <AlertCircle className="w-5 h-5" /> Provoke
                </button>
              </>
            )}

            <button 
              onClick={handleShare}
              className="flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 hover:scale-105 transition-all shadow-sm"
            >
              <Share2 className="w-5 h-5" /> Share Profile
            </button>
          </div>
        </div>
      </div>

      {/* Inactivity / Streak Animated Card */}
      <div className="relative overflow-hidden">
        {daysWithoutStudy > 1 || (daysWithoutStudy === 1 && (!user.streak || user.streak === 0)) ? (
          <div className="bg-gradient-to-br from-amber-50 to-orange-100 border border-orange-200 rounded-3xl p-6 shadow-md flex items-center justify-between group transform transition-transform hover:scale-[1.01]">
            <div>
              <h3 className="text-xl font-bold text-orange-900 mb-1">Time to study!</h3>
              <p className="text-orange-700 font-medium">
                {user.username} is {daysWithoutStudy} day{daysWithoutStudy !== 1 ? 's' : ''} without studying.
              </p>
            </div>
            <div className="text-5xl animate-bounce">😴</div>
          </div>
        ) : daysWithoutStudy === -1 ? (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 border border-indigo-200 rounded-3xl p-6 shadow-md flex items-center justify-between group transform transition-transform hover:scale-[1.01]">
            <div>
              <h3 className="text-xl font-bold text-indigo-900 mb-1">New Learner!</h3>
              <p className="text-indigo-700 font-medium">
                {user.username} hasn't started studying yet. Send a challenge!
              </p>
            </div>
            <div className="text-5xl animate-bounce">👋</div>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-emerald-50 to-teal-100 border border-teal-200 rounded-3xl p-6 shadow-md flex items-center justify-between group transform transition-transform hover:scale-[1.01]">
            <div>
              <h3 className="text-xl font-bold text-teal-900 mb-1">On Fire! 🔥</h3>
              <p className="text-teal-700 font-medium">
                {user.username} has an active streak of {user.streak || 1} day{(user.streak || 1) !== 1 ? 's' : ''}!
              </p>
            </div>
            <div className="text-5xl animate-pulse">🏃‍♂️</div>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <h2 className="text-2xl font-bold text-gray-800 pt-4 px-2">Learning Stats</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
          <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <Clock className="w-6 h-6" />
          </div>
          <p className="text-sm font-medium text-gray-500 mb-1">Days Studied</p>
          <p className="text-2xl font-bold text-gray-900">{stats.daysStudied}</p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
          <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <Gamepad2 className="w-6 h-6" />
          </div>
          <p className="text-sm font-medium text-gray-500 mb-1">Crime Games Solved</p>
          <p className="text-2xl font-bold text-gray-900">{stats.crimeGamesSolved}</p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
          <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <Brain className="w-6 h-6" />
          </div>
          <p className="text-sm font-medium text-gray-500 mb-1">Daily Training</p>
          <p className="text-2xl font-bold text-gray-900">{stats.dailyTrainingDone}</p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
          <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <BookOpen className="w-6 h-6" />
          </div>
          <p className="text-sm font-medium text-gray-500 mb-1">Word of the Day</p>
          <p className="text-2xl font-bold text-gray-900">{stats.wordOfDayDone}</p>
        </div>

      </div>

      {/* Achievements Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 mt-8 mb-8">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Achievements</h2>
          {userAchievements && (
            <span className="bg-indigo-100 text-indigo-800 text-sm font-medium px-3 py-1 rounded-full">
               Level {userAchievements.totalPoints > 1000 ? 'Expert' : userAchievements.totalPoints > 500 ? 'Advanced' : userAchievements.totalPoints > 100 ? 'Intermediate' : 'Beginner'}
            </span>
          )}
        </div>
        
        <div className="p-6">
          {!userAchievements ? (
            <div className="space-y-3 animate-pulse">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center p-3 border border-gray-100 rounded-lg bg-gray-50 h-20">
                  <div className="h-10 w-10 bg-gray-200 rounded-full mr-4"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : userAchievements.achievements && userAchievements.achievements.length > 0 ? (
            <div className="space-y-4">
              <div className="space-y-3">
                {userAchievements.achievements.slice(0, 5).map((achievement: any, idx: number) => (
                  <div key={idx} className="flex items-center p-3 border border-gray-100 rounded-lg bg-gray-50 shadow-sm transform transition-transform hover:scale-[1.01]">
                    <img referrerPolicy="no-referrer" src={achievement.icon} alt={achievement.name || achievement.title} className="h-10 w-10 mr-4" />
                    <div>
                      <h4 className="font-bold text-gray-800">{achievement.name || achievement.title}</h4>
                      <p className="text-sm text-gray-600">{achievement.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 rounded-xl p-6 text-center shadow-sm">
              <div className="text-4xl mb-3">🏆</div>
              <h4 className="text-lg font-bold text-indigo-900 mb-2">No Achievements Yet!</h4>
              <p className="text-gray-600 text-sm">
                {user.username} hasn't unlocked any achievements yet.
              </p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default PublicProfilePage;
